import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { DemoItem } from '@mui/x-date-pickers/internals/demo';
import TextFieldStyled from '../../shared/TextFieldStyled';
import { CustomItem } from '../../../models/quotations/Quotation';
import { styled, useTheme } from '@mui/material/styles';
import {
    Box,
    FormControl,
    InputAdornment,
    MenuItem,
    Skeleton,
    Typography,
} from '@mui/material';
import SelectStyled from 'components/shared/SelectStyled';
import useQuotationsUnitsSWR from 'hooks/quotations/useQuotationsUnitsSWR';
import { ErrorTypography } from 'components/styled/ErrorTypography';
import { formatPrice } from 'utils/numberFormatter';
import DeleteButton from 'components/shared/DeleteButton';

const StyledDeleteSpanContainer = styled(Box)(({ theme }) => ({
    paddingTop: '12px',
    [theme.breakpoints.up('lg')]: {
        paddingTop: '24px',
    },
}));

const StyledBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
    paddingBottom: '20px',
    [theme.breakpoints.up('sm')]: {
        placeItems: 'center',
    },
    [theme.breakpoints.down('lg')]: {
        placeItems: 'flex-end',
        flexDirection: 'column',
    },
}));

const StyledItem = styled(Box)(({ theme }) => ({
    [theme.breakpoints.up('lg')]: {
        flex: 1,
    },
    [theme.breakpoints.down('lg')]: {
        width: '100%',
    },
}));

const TotalsContainer = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
}));

const StyledTotals = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    height: '43px',
    paddingTop: '10px',
    [theme.breakpoints.down('lg')]: {
        height: 'unset',
        paddingTop: 'unset',
    },
}));

export const QuotationCustomItem: React.FC<{
    item: CustomItem;
    index: number;
    fieldsVariant: 'filled' | 'outlined';
    changeFieldsHandle: (itemId: number, content: CustomItem) => void;
    deleteItemHandle: (index: number) => void;
}> = (props) => {
    const { item, index, fieldsVariant, changeFieldsHandle, deleteItemHandle } =
        props;
    const { control, register, getValues, setValue } = useForm<CustomItem>({
        defaultValues: {
            name: item.name,
            net_price: item.net_price,
            quantity: item.quantity,
            unit: item.unit,
            tax: item.tax,
        },
    });

    const [totals, setTotals] = useState<{ [key: string]: string }>({
        gross: formatPrice(item.gross_price) + ' Ft',
        net: formatPrice(item.net_price) + ' Ft',
    });

    useEffect(() => {
        setTotals({
            gross:
                formatPrice(
                    item.net_price * item.quantity * (item.tax / 100 + 1)
                ) + ' Ft',
            net: formatPrice(item.net_price * item.quantity) + ' Ft',
        });
        setValue('name', item.name);
        setValue('net_price', item.net_price);
        setValue('quantity', item.quantity);
        setValue('unit', item.unit);
        setValue('tax', item.tax);
    }, [item]);

    let timer = null;

    const { t } = useTranslation('quotations', {
        keyPrefix: 'QUOTATIONS.QUOTATION_EDITOR.CUSTOM_ITEMS.FIELDS',
    });

    const changedField = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            changeFieldsHandle(index, getValues());
        }, 1000);
    };

    const { units, isLoading, isError } = useQuotationsUnitsSWR();
    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                }}
            >
                <Skeleton height={'20px'} />
                <Skeleton height={'45px'} />
            </Box>
        );
    }

    if (isError) return <ErrorTypography errorCode={'GLOBAL_ERROR'} />;
    return (
        <>
            <StyledBox>
                <StyledItem>
                    <FormControl fullWidth>
                        <DemoItem label={t('NAME')}>
                            <TextFieldStyled
                                sx={{ backgroundColor: 'white' }}
                                {...register('name', {
                                    onChange: changedField,
                                })}
                                variant={fieldsVariant}
                            />
                        </DemoItem>
                    </FormControl>
                </StyledItem>
                <StyledItem>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '10px',
                        }}
                    >
                        <FormControl fullWidth>
                            <DemoItem label={t('QUANTITY')}>
                                <TextFieldStyled
                                    type={'number'}
                                    sx={{ backgroundColor: 'white' }}
                                    {...register('quantity', {
                                        onChange: changedField,
                                    })}
                                    InputProps={{
                                        inputProps: { min: 1 },
                                    }}
                                    variant={fieldsVariant}
                                />
                            </DemoItem>
                        </FormControl>
                        <FormControl fullWidth variant={fieldsVariant}>
                            <DemoItem label={t('MEASUREMENT_UNIT')}>
                                <Controller
                                    name="unit"
                                    control={control}
                                    defaultValue={item.unit ? item.unit : 'db'}
                                    render={({ field }) => (
                                        <SelectStyled
                                            name={field.name}
                                            onChange={(e) => {
                                                field.onChange(e);
                                                changedField();
                                            }}
                                            type={'string'}
                                            sx={{
                                                '& .MuiSelect-select': {
                                                    background: 'white',
                                                },
                                                '& .MuiFilledInput-input': {
                                                    padding:
                                                        '10px 10px 11px 10px !important',
                                                },
                                                '& .MuiOutlinedInput-input': {
                                                    padding:
                                                        '8.5px 14px !important',
                                                },
                                            }}
                                            value={field.value}
                                            defaultValue={''}
                                        >
                                            {units.map((item) => (
                                                <MenuItem
                                                    key={item}
                                                    value={item}
                                                >
                                                    {item}
                                                </MenuItem>
                                            ))}
                                        </SelectStyled>
                                    )}
                                />
                            </DemoItem>
                        </FormControl>
                    </Box>
                </StyledItem>
                <StyledItem>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '10px',
                        }}
                    >
                        <FormControl fullWidth>
                            <DemoItem label={t('NET_PRICE')}>
                                <TextFieldStyled
                                    type={'number'}
                                    sx={{ backgroundColor: 'white' }}
                                    {...register('net_price', {
                                        onChange: changedField,
                                    })}
                                    InputProps={{
                                        inputProps: { min: 1 },
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Typography variant="caption">
                                                    Ft
                                                </Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                    variant={fieldsVariant}
                                />
                            </DemoItem>
                        </FormControl>
                        <FormControl fullWidth>
                            <DemoItem label={t('TAX')}>
                                <TextFieldStyled
                                    type={'number'}
                                    sx={{ backgroundColor: 'white' }}
                                    {...register('tax', {
                                        onChange: changedField,
                                    })}
                                    InputProps={{
                                        inputProps: { min: 1 },
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Typography variant="caption">
                                                    %
                                                </Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                    variant={fieldsVariant}
                                />
                            </DemoItem>
                        </FormControl>
                    </Box>
                </StyledItem>
                <StyledItem>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '10px',
                        }}
                    >
                        <FormControl fullWidth>
                            <TotalsContainer>
                                <DemoItem label={t('NET_TOTAL')}>
                                    <StyledTotals>{totals.net}</StyledTotals>
                                </DemoItem>
                            </TotalsContainer>
                        </FormControl>
                        <FormControl fullWidth>
                            <TotalsContainer
                                sx={{
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <DemoItem label={t('GROSS_TOTAL')}>
                                    <StyledTotals>{totals.gross}</StyledTotals>
                                </DemoItem>
                                <StyledDeleteSpanContainer>
                                    <DeleteButton
                                        onClick={() => deleteItemHandle(index)}
                                    />
                                </StyledDeleteSpanContainer>
                            </TotalsContainer>
                        </FormControl>
                    </Box>
                </StyledItem>
            </StyledBox>
        </>
    );
};
